import { resolveEventRange } from "@/utils/date";

const ONE_HOUR_MS = 60 * 60 * 1000;

const pad = (num: number) => String(num).padStart(2, "0");

const toUtcDate = (d: Date) =>
  `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`;

const toUtcInstant = (d: Date) =>
  `${toUtcDate(d)}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;

/** `YYYY-MM-DD` as `YYYYMMDD`. */
const toCompactDate = (date: string) => date.replace(/-/g, "");

/** The day after a `YYYY-MM-DD` date, as `YYYYMMDD`. */
function dayAfter(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  return toUtcDate(new Date(Date.UTC(year, month - 1, day + 1)));
}

/**
 * The `dates` value for a Google Calendar template link. Events with times
 * become UTC instants, which Google shows in the viewer's own zone. Date-only
 * events become an all-day range built from the calendar dates themselves,
 * never from an instant, with the end one day later because Google's all-day
 * end is exclusive.
 */
function googleCalendarDates(opts: {
  startDate: string;
  endDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
}): string {
  const range = resolveEventRange(opts);
  if (range.includeTime) {
    const start = new Date(range.start);
    const end = range.end
      ? new Date(range.end)
      : new Date(start.getTime() + ONE_HOUR_MS);
    return `${toUtcInstant(start)}/${toUtcInstant(end)}`;
  }
  return `${toCompactDate(opts.startDate)}/${dayAfter(opts.endDate || opts.startDate)}`;
}

export function buildGoogleCalendarUrl(opts: {
  eventName: string;
  hostName: string;
  locationText?: string | null;
  startDate: string;
  endDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
}): string {
  const dates = googleCalendarDates(opts);
  const text = encodeURIComponent(opts.eventName);
  const details = encodeURIComponent(`${opts.eventName} — ${opts.hostName}`);
  const location = encodeURIComponent(opts.locationText || "");
  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${dates}&details=${details}&location=${location}`;
}
